// Tạo file mới: src/utils/shipmentCopyUtils.js

import { SHIPMENT_DETAIL_FIELDS, DRIVER_FIELDS, TRUCK_FIELDS } from '../constants';

/**
 * Format thông tin chuyến xe để copy gửi cho khách hàng
 * @param {object} shipmentDetail - Chi tiết chuyến xe
 * @param {object} driver - Thông tin lái xe
 * @param {object} truck - Thông tin xe
 * @returns {string} Formatted text để copy
 */
export const formatShipmentInfoForCustomer = (shipmentDetail, driver, truck) => {
  if (!shipmentDetail || !driver || !truck) {
    return 'Không đủ thông tin để tạo nội dung';
  }

  // Format ngày sinh
  const formatBirthDate = (dateString) => {
    if (!dateString) return 'N/A';
    // Chuyển từ DD-MM-YYYY sang DD.MM.YYYY
    return dateString.replace(/-/g, '.');
  };

  // Format ngày cấp CCCD
  const formatIssueDate = (dateString) => {
    if (!dateString) return 'N/A';
    // Chuyển từ DD-MM-YYYY sang DD.MM.YYYY
    return dateString.replace(/-/g, '.');
  };

  // Lấy thông tin từ driver
  const driverName = driver[DRIVER_FIELDS.NAME] || 'N/A';
  const driverPhone = driver[DRIVER_FIELDS.PHONE] || 'N/A';
  const driverBirthDate = formatBirthDate(driver[DRIVER_FIELDS.BIRTH_DATE] || shipmentDetail.ngaysinh);
  const driverIdCard = driver[DRIVER_FIELDS.ID_CARD] || shipmentDetail.CCCD || 'N/A';
  const driverAddress = driver[DRIVER_FIELDS.ADDRESS] || shipmentDetail.diachithuongtru || 'N/A';
  
  // Thông tin xe
  const licensePlate = truck[TRUCK_FIELDS.LICENSE_PLATE] || shipmentDetail.bienso || 'N/A';
  const registrationNumber = shipmentDetail.sodangky || shipmentDetail[SHIPMENT_DETAIL_FIELDS.REGISTRATION_NUMBER] || '0';
  const containerInfo = shipmentDetail.socuathung || shipmentDetail[SHIPMENT_DETAIL_FIELDS.CONTAINER_NUMBER] || '2 Cửa - Thùng Kín';

  // Ngày cấp CCCD và nơi cấp (có thể lấy từ shipmentDetail hoặc driver)
  const idIssueDate = formatIssueDate(shipmentDetail.ngaycapCCCD || 'N/A');
  const idIssuePlace = shipmentDetail.noicapCCCD || 'CCSQLHCVTTXH';

  const formattedText = `**Biển Số Xe: ${licensePlate}**
**Họ Và Tên: ${driverName}**
**Ngày Sinh: ${driverBirthDate}**
**Số CMT/CCCD: ${driverIdCard}**
**Ngày Cấp: ${idIssueDate}**
**Nơi Cấp: ${idIssuePlace}**
**Số điện thoại: ${driverPhone}**
**Quê Quán: ${driverAddress}**
**Số đăng ký xe: ${registrationNumber}**
**Số cửa thùng xe: ${containerInfo}**`;

  return formattedText;
};

/**
 * Copy text to clipboard
 * @param {string} text - Text to copy
 * @returns {Promise<boolean>} Success status
 */
export const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      // Use modern clipboard API
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      // Fallback for older browsers or non-secure contexts
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      return successful;
    }
  } catch (err) {
    console.error('Failed to copy text: ', err);
    return false;
  }
};

/**
 * Format thông tin giao hàng để copy
 * @param {object} shipmentDetail - Chi tiết chuyến xe
 * @returns {string} Formatted delivery info
 */
export const formatDeliveryInfoForCustomer = (shipmentDetail) => {
  if (!shipmentDetail) return 'Không có thông tin giao hàng';

  let deliveryInfo = `**THÔNG TIN GIAO HÀNG**\n`;
  deliveryInfo += `**Ngày nhận hàng: ${shipmentDetail[SHIPMENT_DETAIL_FIELDS.PICKUP_DATE] || 'N/A'}**\n`;
  
  // Địa chỉ lấy hàng
  if (shipmentDetail[SHIPMENT_DETAIL_FIELDS.PICKUP_ADDRESS]) {
    deliveryInfo += `**Địa chỉ lấy hàng: ${shipmentDetail[SHIPMENT_DETAIL_FIELDS.PICKUP_ADDRESS]}**\n`;
  }

  // Các điểm giao hàng
  for (let i = 1; i <= 4; i++) {
    const deliveryAddress = shipmentDetail[SHIPMENT_DETAIL_FIELDS[`DELIVERY_ADDRESS_${i}`]];
    const deliveryDate = shipmentDetail[SHIPMENT_DETAIL_FIELDS[`DELIVERY_DATE_${i}`]];
    
    if (deliveryAddress) {
      deliveryInfo += `**Điểm giao ${i}: ${deliveryAddress}**\n`;
      if (deliveryDate) {
        deliveryInfo += `**Ngày giao ${i}: ${deliveryDate}**\n`;
      }
    }
  }

  return deliveryInfo;
};

/**
 * Format thông tin đầy đủ chuyến xe (driver + delivery)
 * @param {object} shipmentDetail - Chi tiết chuyến xe
 * @param {object} driver - Thông tin lái xe
 * @param {object} truck - Thông tin xe
 * @returns {string} Complete formatted info
 */
export const formatCompleteShipmentInfo = (shipmentDetail, driver, truck) => {
  const driverInfo = formatShipmentInfoForCustomer(shipmentDetail, driver, truck);
  const deliveryInfo = formatDeliveryInfoForCustomer(shipmentDetail);
  
  return `${driverInfo}\n\n${deliveryInfo}`;
};